<template>
  <div class="media-owners-legend">
    <div
      class="header"
      :class="{ opened: showMediaOwnerOptions }"
      @click="toggleMediaOwnerOptionsView">
      <div class="left">
        <MediaOwnerIcon />
        <p>{{ capitalize($t('names.mediaOwner', { n: 2 })) }}</p>
      </div>
      <SectionCollapse v-if="showMediaOwnerOptions" />
      <SectionExpand v-else />
    </div>

    <Transition>
      <template v-if="showMediaOwnerOptions">
        <ul
          v-if="mediaOwnerOptions?.length"
          :class="{ selected: activeMediaOwnerOption }">
          <li
            v-for="o in orderedMediaOwnerOptions"
            :key="o.id"
            :class="{ selected: activeMediaOwnerOption?.id === o.id }"
            @click="onMediaOwnerClick(o)">
            <template v-if="o.mediaOwner.logoUrl">
              <img
                v-tooltip="o.mediaOwner.name"
                :src="o.mediaOwner.logoUrl"
                :alt="o.mediaOwner.name" />
            </template>
            <template v-else>
              <p>
                {{ o.mediaOwner.name }}
              </p>
            </template>

            <span class="count">
              {{ formatNumber(o.unitCount, '0,0') }}
              <Screen />
            </span>
          </li>
        </ul>

        <div v-else>
          <p class="no-clusters-in-view">
            {{ noUnitClustersInView }}
          </p>
        </div>
      </template>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref } from 'vue';

  import MediaOwnerIcon from '@/common/icons/colored/MediaOwner.vue';
  import Screen from '@/common/icons/monochrome/Screen.vue';
  import SectionCollapse from '@/common/icons/monochrome/SectionCollapse.vue';
  import SectionExpand from '@/common/icons/monochrome/SectionExpand.vue';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import { formatNumber, orderBy } from '@/globals';
  import MediaOwnerOption from '@/org-app/components/geofenceGroups/media-owner-legend/MediaOwnerOption.model';
  import i18n from '@/vueI18n';

  interface Props {
    mediaOwnerOptions: MediaOwnerOption[];
    onMediaOwnerSelected: (mediaOwner: MediaOwner) => void;
    isOpened: (isOpen: boolean) => void;
  }

  const props = defineProps<Props>();
  const showMediaOwnerOptions = ref(false);
  const activeMediaOwnerOption = ref<MediaOwnerOption | null>(null);

  const orderedMediaOwnerOptions = computed<MediaOwnerOption[]>(() =>
    orderBy(props.mediaOwnerOptions, 'unitCount').reverse(),
  );
  const noUnitClustersInView = computed<string>(() => i18n.global.t('messages.noUnitClustersInView'));

  function onMediaOwnerClick(mediaOwnerOption: MediaOwnerOption) {
    if (mediaOwnerOption.id === activeMediaOwnerOption.value?.id) {
      activeMediaOwnerOption.value = null;
      props.onMediaOwnerSelected(null);
      return;
    }

    activeMediaOwnerOption.value = mediaOwnerOption;
    props.onMediaOwnerSelected(mediaOwnerOption.mediaOwner);
  }

  function toggleMediaOwnerOptionsView() {
    showMediaOwnerOptions.value = !showMediaOwnerOptions.value;
    props.isOpened(showMediaOwnerOptions.value);

    if (!showMediaOwnerOptions.value) {
      activeMediaOwnerOption.value = null;
      props.onMediaOwnerSelected(null, []);
    }
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .media-owners-legend {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 3px;
    position: relative;

    p {
      margin-bottom: 0;

      &.no-clusters-in-view {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }

    .header {
      display: flex;
      justify-content: space-between;
      cursor: pointer;
      align-items: center;
      width: 100%;
      background: white;
      padding: 1rem;

      .left {
        display: flex;
        align-items: center;

        svg {
          margin-right: 1rem;
        }
      }

      &.opened {
        border-bottom: 1px solid var(--borderColor);
        position: sticky;
        top: 0;
      }

      svg {
        stroke: var(--textColor);
        width: 1.6rem;
        height: 1.6rem;
      }
    }

    .v-enter-active,
    .v-leave-active {
      transition: opacity 0.5s ease;
    }

    .v-enter-from,
    .v-leave-to {
      opacity: 0;
    }

    ul {
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      flex-direction: column;

      &.selected {
        li {
          opacity: 0.5;

          &.selected {
            border-left: solid 0.3rem var(--actionsColor);
          }

          &.selected,
          &:hover {
            opacity: 1;
          }

          &:hover {
            cursor: pointer;
          }
        }
      }

      li {
        height: 3rem;
        list-style: none;
        margin: 0.3rem 0;
        padding: 0.3rem 1rem;
        cursor: pointer;
        display: grid;
        grid-template-columns: 3fr 1fr;
        column-gap: 2rem;
        border-left: solid 0.3rem transparent;

        &:hover {
          border-left: solid 0.3rem var(--textMutedColor);
        }

        p {
          display: flex;
          align-items: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 0.8em;
        }

        img {
          max-height: 2rem;
          max-width: 100%;
        }

        .count {
          margin-left: 1rem;
          display: flex;
          align-items: center;
          font-family: $monospace-font-family;
          font-size: 0.8em;
          justify-content: flex-end;

          svg {
            stroke: var(--textMutedColor);
            width: 1.2rem;
            height: 1.2rem;
            margin-left: 0.6rem;
          }
        }
      }
    }
  }
</style>
